import { useEffect } from 'react';

const SITE_NAME = 'JetScanner360';
const DEFAULT_DESCRIPTION = 'JetScanner360 - Private Flight Booking Platform';
const DEFAULT_OG_IMAGE = 'https://jetscanner360.com/jetscanner-logo.svg';

const absoluteUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (typeof window !== 'undefined' && url.startsWith('/')) {
    return `${window.location.origin}${url}`;
  }
  return url;
};

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const formatTitle = (title) => {
  if (!title) return SITE_NAME;
  return title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
};

/**
 * Sets document title + meta description + Open Graph / Twitter tags in <head>.
 * Inspect Elements → <head> to verify (SPA View Source still shows index.html defaults).
 */
const SEOHead = ({
  title,
  description,
  ogTitle,
  ogDescription,
  keywords,
  image,
  url,
  type = 'website',
  siteName = SITE_NAME,
  noIndex = false,
}) => {
  useEffect(() => {
    const pageTitle = formatTitle(title);
    const metaDescription = (description || DEFAULT_DESCRIPTION).replace(/\s+/g, ' ').trim().slice(0, 300);
    const openGraphTitle = formatTitle(ogTitle || title);
    const openGraphDescription = (ogDescription || description || DEFAULT_DESCRIPTION)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 300);
    const ogImage = absoluteUrl(image) || DEFAULT_OG_IMAGE;
    const canonicalUrl = url || window.location.href;

    document.title = pageTitle;

    upsertMeta('name', 'description', metaDescription);
    if (keywords) upsertMeta('name', 'keywords', keywords);
    upsertMeta('name', 'robots', noIndex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', canonicalUrl);

    upsertMeta('property', 'og:title', openGraphTitle);
    upsertMeta('property', 'og:description', openGraphDescription);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:type', type || 'website');
    upsertMeta('property', 'og:site_name', siteName);

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', openGraphTitle);
    upsertMeta('name', 'twitter:description', openGraphDescription);
    upsertMeta('name', 'twitter:image', ogImage);
  }, [title, description, ogTitle, ogDescription, keywords, image, url, type, siteName, noIndex]);

  return null;
};

export default SEOHead;
export { SITE_NAME, DEFAULT_DESCRIPTION, DEFAULT_OG_IMAGE };
